import { forwardRef, type HTMLAttributes } from 'react';
import clsx from 'clsx';

import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Skeleton.module.css';

export interface SkeletonProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** `text` draws `lines` bars at --c-skeleton-h; `block` fills its box. */
  variant?: 'text' | 'block';
  lines?: number;
  /** Any CSS length. Pass a token (`token.layout.panelSide`) or a percentage. */
  width?: string;
  /** Only meaningful for `block`. */
  height?: string;
}

/**
 * Always `aria-hidden`: a placeholder has nothing to announce. The container
 * that is loading owns the live region (`Spinner`, or `role="status"` on the
 * panel), so a screen reader hears "loading" once, not once per bar.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { variant = 'text', lines = 3, width, height, className, style, ...rest },
  ref,
) {
  const vars: StyleWithVars = {};
  if (width !== undefined) vars['--dpem-skeleton-w'] = width;
  if (height !== undefined) vars['--dpem-skeleton-h'] = height;

  if (variant === 'block') {
    return (
      <div
        {...rest}
        ref={ref}
        aria-hidden="true"
        className={clsx(styles.bar, styles.block, className)}
        style={mergeStyle(vars, style)}
      />
    );
  }

  return (
    <div
      {...rest}
      ref={ref}
      aria-hidden="true"
      className={clsx(styles.text, className)}
      style={mergeStyle(vars, style)}
    >
      {Array.from({ length: Math.max(1, lines) }, (_, index) => (
        <div
          key={index}
          className={clsx(styles.bar, index === lines - 1 && lines > 1 && styles.last)}
        />
      ))}
    </div>
  );
});
